import { useDisclosure } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { useDeleteContactMutation } from "@src/__generated__/graphql";
import {
  ExpandableItem,
  type ExpandableItemProps,
  LabeledItem,
  ModalConfirm,
} from "@src/components/ui-kit";
import { ModalContactPerson } from "@src/components/widgets/Modals/ModalContactPerson";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FC, Fragment, useMemo } from "react";
import type { TContact, TContactExtended } from "./types";

export interface SectionContactProps {
  contact: TContactExtended;
  onRemoveContact: (id: string) => void;
  onUpdateContact: (contact: TContact) => void;
}

const SectionContact: FC<React.PropsWithChildren<SectionContactProps>> = ({
  contact,
  onRemoveContact,
  onUpdateContact,
}) => {
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();

  const [del] = useDeleteContactMutation({
    onCompleted(data) {
      if (data?.deleteContact) {
        onRemoveContact(data.deleteContact.id);
      }
    },
    ...onError(),
  });

  const popoverActions = useMemo(() => {
    const actions: ExpandableItemProps["popoverActions"] = [];

    can("brand_update") &&
      actions.push({
        content: t`Edit`,
        iconName: "edit-02",
        onAction: editModal.onOpen,
      });

    can("brand_delete") &&
      actions.push({
        content: t`Delete`,
        iconName: "trash-03",
        onAction: deleteModal.onOpen,
      });

    return actions;
  }, []);

  const pipedriveCustomFieldValues = useMemo(
    () =>
      (contact.pipedriveCustomFieldValues ?? []).filter(
        (item) => !!item.customField.label && !!item.value,
      ),
    [contact.pipedriveCustomFieldValues],
  );

  return (
    <Fragment>
      <ExpandableItem
        title={`${contact.fullName} (${contact.email})`}
        popoverActions={popoverActions}
      >
        <div>
          <LabeledItem label={t`First name`}>{contact.first_name}</LabeledItem>
          <LabeledItem label={t`Last name`}>{contact.last_name}</LabeledItem>
          <LabeledItem label={t`Email`}>
            <a
              className="text-turquoiseDark"
              target="_blank"
              href={`mailto:${contact.email}`}
              rel="noreferrer"
            >
              {contact.email}
            </a>
          </LabeledItem>
          <LabeledItem label={t`Phone`}>{contact.phone}</LabeledItem>
          {pipedriveCustomFieldValues.map((item) => (
            <LabeledItem
              key={`${item.customField.label}-${item.value}`}
              label={item.customField.label ?? ""}
            >
              {item.value}
            </LabeledItem>
          ))}
        </div>
      </ExpandableItem>
      <ModalContactPerson
        {...editModal}
        contact={contact}
        title={t`Edit contact`}
        onSubmit={onUpdateContact}
      />
      <ModalConfirm
        {...deleteModal}
        onConfirm={() => del({ variables: { id: contact.id } })}
      />
    </Fragment>
  );
};

export default observer(SectionContact);
